import css from './ExtraCardsContainer.module.scss'
import React, { useRef, useState } from 'react'
import { useEffect } from 'react'
import { EffectCoverflow } from 'swiper/modules'
import { Swiper, SwiperClass, SwiperSlide } from 'swiper/react'
import { GoodTicketCode } from 'types/tickets'
import { v4 as uuidv4 } from 'uuid'

import { IGoodServiceTicketData, ISelectableGoodServiceTicket, Tap2GOStatus } from 'meta/pages/goodsandservices'
import { IEventData } from 'meta/pages/v4/event'
import { ILocationData } from 'meta/pages/v4/location'
import { IWalletEvent } from 'meta/pages/v4/wallet'
import { useAppDispatch } from 'store'
import { CheckoutActions } from 'store/pages/checkout'

import Modal from 'components/Layout/Modal'
import GSFullDescriptionModal from 'components/Pages/GoodsAndServices/GSFullDescriptionModal'
import GSMultipleItemsModal from 'components/Pages/GoodsAndServices/GSMultipleItemsModal'
import GSQRCodeModal from 'components/Pages/GoodsAndServices/GSQRCodeModal'
import Tap2GoMultipleModal from 'components/Pages/GoodsAndServices/Tap2GOMultipleModal'
import SliderProgress from 'components/Pages/GoodsAndServicesTransactions/SliderProgress'
import AddExtra from 'components/Pages/Wallet/AddExtra'
import { useWalletEventTicketCodes } from 'hooks/useWallet'

import AddExtraCard from '../AddExtraCard'
import ExtraCardContainer from '../ExtraCard/ExtraCardContainer'

interface Props {
  walletEventData: IWalletEvent
  eventData: IEventData
  locationData: ILocationData
}

const ExtraCardsContainer: React.FC<Props> = (props) => {
  const { walletEventData, eventData, locationData } = props
  const { id, count } = walletEventData

  const [showTap2GoModal, setShowTap2GoModal] = useState<boolean>(false)
  const [showQRCodeModal, setShowQRCodeModal] = useState<boolean>(false)
  const [showMultipleItemsModal, setShowMultipleItemsModal] = useState<boolean>(false)
  const [initialUuid, setInitialUuid] = useState<string>('')
  const [showFullDescriptionModal, setShowFullDescriptionModal] = useState<boolean>(false)
  const [gsActiveCardData, setGsActiveCardData] = useState<IGoodServiceTicketData | null>(null)
  const [activeCard, setActiveCard] = useState<number>(0)
  const [checkedAt, setCheckedAt] = useState<string | undefined>(undefined)

  const [tap2GoStatus, setTap2GoStatus] = useState<Tap2GOStatus>(Tap2GOStatus.request)

  const [availableTicketsData, setAvailableTicketsData] = useState<Array<ISelectableGoodServiceTicket>>([])
  const [selectedTicketsData, setSelectedTicketsData] = useState<Array<ISelectableGoodServiceTicket>>([])
  const [checkedTicketsIds, setCheckedTicketsIds] = useState<Array<number>>([])
  const [unusableTicketsIds, setUnusableTicketsIds] = useState<Array<number>>([])

  const swiperClassRef = useRef<SwiperClass>(null)

  const dispatch = useAppDispatch()

  const eventTicketCodesData = useWalletEventTicketCodes({ eventIdOrCodeUrl: String(id) })

  const [showAddExtraBalloon, setShowAddExtraBalloon] = useState<boolean>(false)

  const openCheckout = () => {
    setShowAddExtraBalloon(false)
    dispatch(CheckoutActions.init({ codeUrl: eventData.codeUrl }))
  }

  const handleAvailableTicketData = (elem: ISelectableGoodServiceTicket, remove?: boolean) => {
    let updatedAvailableTicketData: ISelectableGoodServiceTicket[] = [...availableTicketsData]

    if (remove) {
      updatedAvailableTicketData = [...availableTicketsData].filter((x) => x.idx !== elem.idx)
    } else {
      updatedAvailableTicketData.push(elem)
    }

    setAvailableTicketsData(updatedAvailableTicketData)
  }

  const handleSelectedTicketData = (elem: ISelectableGoodServiceTicket, remove?: boolean) => {
    let updatedSelectedTicketData: ISelectableGoodServiceTicket[] = [...selectedTicketsData]

    if (remove) {
      if (selectedTicketsData.length == 1) {
        updatedSelectedTicketData = []
      } else {
        updatedSelectedTicketData = [...selectedTicketsData].filter((x) => x.idx !== elem.idx)
      }
    } else {
      updatedSelectedTicketData.push(elem)
    }

    setSelectedTicketsData(updatedSelectedTicketData)
  }

  const handleTap2GoModalClose = () => {
    if (tap2GoStatus == Tap2GOStatus.success) {
      if (selectedTicketsData.length > 0) {
        setCheckedTicketsIds(selectedTicketsData.map((x) => x.idx).concat(checkedTicketsIds))
      } else {
        setCheckedTicketsIds([activeCard].concat(checkedTicketsIds))
      }

      setAvailableTicketsData([])
    }

    setShowTap2GoModal(false)
    setTap2GoStatus(Tap2GOStatus.request)
    setSelectedTicketsData([])
  }

  const handleUnusableTicketIds = (idx: number) => {
    setUnusableTicketsIds(unusableTicketsIds.concat(idx))
  }

  useEffect(() => {
    setInitialUuid(uuidv4())
  }, [])

  if (!eventTicketCodesData) {
    return null
  }

  return (
    <div className={css.wrapper}>
      <Swiper
        effect={'coverflow'}
        grabCursor={true}
        centeredSlides={true}
        slidesPerView={'auto'}
        coverflowEffect={{
          rotate: 50,
          stretch: 10,
          depth: 300,
          modifier: 1,
          slideShadows: false,
        }}
        modules={[EffectCoverflow]}
        pagination={false}
        className={css.swiper}
        onSlideChange={(swiper) => setActiveCard(swiper.activeIndex)}
        onAfterInit={(swiper) => {
          //@ts-expect-error
          swiperClassRef.current = swiper
        }}
      >
        {eventTicketCodesData.map((event, key) => {
          return (
            <SwiperSlide className={css.card} key={key}>
              <ExtraCardContainer
                cod={event.cod}
                idx={key}
                eventData={eventData}
                locationData={locationData}
                setShowTap2GoModal={(data: IGoodServiceTicketData) => {
                  setGsActiveCardData(data)

                  if (eventTicketCodesData.length - unusableTicketsIds.length > 1) {
                    setShowMultipleItemsModal(true)
                  } else {
                    setShowTap2GoModal(true)
                  }
                }}
                setShowQRCodeModal={(data: IGoodServiceTicketData) => {
                  setGsActiveCardData(data)
                  setShowQRCodeModal(true)
                }}
                setShowFullDescriptionModal={(data: IGoodServiceTicketData) => {
                  setGsActiveCardData(data)
                  setShowFullDescriptionModal(true)
                }}
                activeCard={activeCard}
                setUnusableTicketId={handleUnusableTicketIds}
                handleAvailableTicketData={handleAvailableTicketData}
                availableTicketIds={availableTicketsData.map((x) => x.idx)}
                checkedTicketdIds={checkedTicketsIds}
                unusableTicketIds={unusableTicketsIds}
                checkedAt={checkedAt}
              />
            </SwiperSlide>
          )
        })}

        <SwiperSlide className={css.card} key={eventTicketCodesData.length + 1}>
          <AddExtraCard
            showAddExtraModal={openCheckout}
            extraType={eventData?.policy?.code.toLowerCase() as GoodTicketCode}
          />
        </SwiperSlide>
      </Swiper>

      <div className={css.progress}>
        <SliderProgress
          activeCard={activeCard}
          totalCards={count}
          setActiveCard={(cardNumber: number) => swiperClassRef?.current?.slideTo(cardNumber)}
          unusableTicketIds={unusableTicketsIds}
          addExtra
        />
      </div>

      {showQRCodeModal && gsActiveCardData && (
        <Modal
          closeIconPath="/icons/close_icon_white_smallcross.svg"
          transparent
          noBodyFixed
          show
          fullWidth
          limitDesktopWidth
          disableScroll
          handleClose={() => setShowQRCodeModal(false)}
        >
          <GSQRCodeModal data={gsActiveCardData} />
        </Modal>
      )}

      {showFullDescriptionModal && gsActiveCardData && (
        <Modal
          closeIconPath="/icons/close_icon_white_smallcross.svg"
          transparent
          noBodyFixed
          show
          fullWidth
          limitDesktopWidth
          disableScroll
          handleClose={() => setShowFullDescriptionModal(false)}
        >
          <GSFullDescriptionModal data={gsActiveCardData} />
        </Modal>
      )}

      {showTap2GoModal && gsActiveCardData && (
        <Modal
          show
          noBodyFixed
          hideCloseIcon
          fullWidth
          transparent
          limitDesktopWidth
          disableScroll
          overflowY
          noPadding
          handleClose={handleTap2GoModalClose}
        >
          <Tap2GoMultipleModal
            initialUuid={initialUuid}
            ticketsData={selectedTicketsData.length > 0 ? selectedTicketsData.map((x) => x.data) : [gsActiveCardData]}
            setCheckedAt={(checkedAt: string) => setCheckedAt(checkedAt)}
            onClose={handleTap2GoModalClose}
            tap2GoStatus={tap2GoStatus}
            setTap2GoStatus={setTap2GoStatus}
          />
        </Modal>
      )}

      {showMultipleItemsModal && availableTicketsData && gsActiveCardData && (
        <Modal
          show
          noBodyFixed
          hideCloseIcon
          fullWidth
          transparent
          limitDesktopWidth
          disableScroll
          overflowY
          noPadding
          handleClose={() => {
            setShowMultipleItemsModal(false)
            setSelectedTicketsData([])
          }}
        >
          <GSMultipleItemsModal
            activeCardData={{
              idx: activeCard,
              data: gsActiveCardData,
            }}
            ticketsData={availableTicketsData}
            handleSelectedTicketData={handleSelectedTicketData}
            onClose={() => {
              setShowMultipleItemsModal(false)
              setSelectedTicketsData([])
            }}
            onConfirm={() => {
              setShowMultipleItemsModal(false)
              setShowTap2GoModal(true)
            }}
            selectedTicketIds={selectedTicketsData.map((x) => x.idx)}
          />
        </Modal>
      )}

      {showAddExtraBalloon && (
        <Modal
          closeIconPath="/icons/close_icon_white_smallcross.svg"
          transparent
          show={true}
          fullWidth={true}
          handleClose={() => setShowAddExtraBalloon(false)}
        >
          <AddExtra openCheckout={openCheckout} />
        </Modal>
      )}
    </div>
  )
}

export default ExtraCardsContainer
